import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@clerk/react";
import { Check, Loader2 } from "lucide-react";
import { API, useApi, errMsg } from "@/lib/api";
import { ProPlans } from "@/components/ProPlans";

const perks = {
  pack_5: ["Hasta 5 envíos a medios", "O 1 canción destacada + envíos"],
  pack_15: ["10% de descuento", "Ideal para un lanzamiento", "Destaca y envía a varios medios"],
  pack_40: ["20% de descuento", "Campañas completas", "Para sellos y managers"],
};

export default function Credits() {
  const [packs, setPacks] = useState([]);
  const [busy, setBusy] = useState("");
  const [mp, setMp] = useState(null);
  const { isLoaded, isSignedIn } = useAuth();
  const api = useApi();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/packages`).then((r) => setPacks(r.data)).catch(() => {});
    axios.get(`${API}/payments/providers`).then((r) => setMp(r.data.mercadopago ? r.data : null)).catch(() => {});
  }, []);

  const buy = async (id, provider = "stripe") => {
    if (!isLoaded) return;
    if (!isSignedIn) { navigate("/sign-up"); return; }
    setBusy(`${provider}-${id}`);
    try {
      const { checkout_url } = await api("post", provider === "mp" ? "/payments/mp/checkout" : "/payments/checkout", { package_id: id, origin_url: window.location.origin });
      window.location.href = checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(""); }
  };

  return (
    <div data-testid="credits-page" className="mx-auto max-w-[1400px] px-4 py-16 md:px-8">
      <p className="font-mono text-xs tracking-widest text-amber-500/90">CRÉDITOS</p>
      <h1 className="mt-4 max-w-3xl font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">Paga solo por lo que envías.</h1>
      <p className="mt-4 max-w-xl text-slate-400">1 crédito = USD 2. Enviar a un medio cuesta de 1 a 3 créditos y destacar tu canción en la radio cuesta 3 créditos por 7 días. Postular a la radio es gratis.</p>
      <div className="mt-14 grid gap-5 md:grid-cols-3">
        {packs.map((p) => {
          const hot = p.id === "pack_15";
          return (
            <div key={p.id} data-testid={`package-${p.id}`} className={`relative flex flex-col rounded-2xl border p-8 ${hot ? "border-[#FF4500]/60 bg-[#181824]" : "border-white/10 bg-[#12121A]"}`}>
              {hot && <span className="absolute -top-3 left-8 rounded-full bg-[#FF4500] px-3 py-1 font-mono text-[10px] tracking-widest text-white">MÁS ELEGIDO</span>}
              <p className="font-mono text-xs tracking-widest text-slate-400">{p.label.toUpperCase()}</p>
              <p className="mt-4 font-heading text-5xl font-extrabold text-white">{p.credits}<span className="ml-2 text-lg font-bold text-slate-400">créditos</span></p>
              <p className="mt-2 text-slate-300">USD {p.price_usd} · <span className="text-slate-500">USD {(p.price_usd / p.credits).toFixed(2)} por crédito</span></p>
              {mp && mp.mercadopago_currency !== "USD" && <p data-testid={`package-local-${p.id}`} className="mt-1 text-sm text-sky-300">{mp.mercadopago_currency} {Number(mp.mercadopago_prices[p.id]).toLocaleString("es-AR")} con Mercado Pago</p>}
              <ul className="mt-6 space-y-2 text-sm text-slate-400">
                {perks[p.id]?.map((t) => <li key={t} className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-400" />{t}</li>)}
              </ul>
              <button data-testid={`buy-${p.id}-btn`} disabled={!!busy} onClick={() => buy(p.id)} className={`mt-8 flex items-center justify-center gap-2 rounded-full py-3 font-semibold transition-colors disabled:opacity-60 ${hot ? "bg-[#FF4500] text-white hover:bg-[#ff5a1f]" : "bg-white text-black hover:bg-slate-200"}`}>
                {busy === `stripe-${p.id}` && <Loader2 className="h-4 w-4 animate-spin" />} Comprar con tarjeta
              </button>
              {mp && (
                <button data-testid={`buy-mp-${p.id}-btn`} disabled={!!busy} onClick={() => buy(p.id, "mp")} className="mt-3 flex items-center justify-center gap-2 rounded-full border border-sky-400/40 py-3 font-semibold text-sky-300 transition-colors hover:bg-sky-400/10 disabled:opacity-60">
                  {busy === `mp-${p.id}` && <Loader2 className="h-4 w-4 animate-spin" />} Pagar con Mercado Pago · {mp.mercadopago_currency} {Number(mp.mercadopago_prices[p.id]).toLocaleString("es-AR")}
                </button>
              )}
            </div>
          );
        })}
      </div>
      <ProPlans />
      <p className="mt-8 text-xs text-slate-500">Pagos seguros con Stripe (modo prueba: usa la tarjeta 4242 4242 4242 4242).{mp ? " También puedes pagar con Mercado Pago." : " Pago con Mercado Pago: próximamente."}</p>
    </div>
  );
}
